import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Grid,
  Typography,
  Button,
} from "@mui/material";
import { Timestamp } from "firebase/firestore";
import useFirestore from "../../../../config/fireBaseHook"; // Import useFirestore
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";

const Edit = ({ open, onClose, ctag, onRecordUpdated }) => {
  // Use Firestore hook with the collection name "TpCtagCatalog"
  // Assuming your hook provides updateDocument and deleteDocument
  const { updateData, deleteData } = useFirestore("TpCtagCatalog");
  const [formData, setFormData] = useState(ctag);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    // Set formData when ctag prop changes.
    // Assumes ctag object from Firestore (passed from Ctag.js) has ODHE_Portal_In, etc., as numbers (-1 or 0).
    if (ctag) {
      const updatedCtag = { ...ctag };

      // Calculate Sinclair_Course_and_Subject if not already set
      // Formula: [@field:Course]+': '+[@field:Subject]
      if (updatedCtag.Sinclair_Course && updatedCtag.Sinclair_Subject_Long) {
        updatedCtag.Sinclair_Course_and_Subject = `${updatedCtag.Sinclair_Course}: ${updatedCtag.Sinclair_Subject_Long}`;
      }

      // Calculate Colleague_CTAN if not already set and CTAN exists
      if (updatedCtag.CTAN && updatedCtag.CTAN.length >= 4) {
        const ctan = updatedCtag.CTAN.toString();
        const left4 = ctan.substring(0, 4);
        const remaining = ctan.substring(4);
        updatedCtag.Colleague_CTAN = `${left4}*${remaining}`;
      }

      // Format date fields for HTML date inputs (YYYY-MM-DD format)
      if (updatedCtag.Colleague_Start_Date) {
        if (updatedCtag.Colleague_Start_Date.toDate) {
          // Firestore Timestamp
          const startDate = updatedCtag.Colleague_Start_Date.toDate();
          updatedCtag.Colleague_Start_Date = startDate
            .toISOString()
            .split("T")[0];
        } else if (updatedCtag.Colleague_Start_Date instanceof Date) {
          // JavaScript Date object
          updatedCtag.Colleague_Start_Date =
            updatedCtag.Colleague_Start_Date.toISOString().split("T")[0];
        } else if (typeof updatedCtag.Colleague_Start_Date === "string") {
          // String - try to convert to proper format
          const dateObj = new Date(updatedCtag.Colleague_Start_Date);
          if (!isNaN(dateObj.getTime())) {
            updatedCtag.Colleague_Start_Date = dateObj
              .toISOString()
              .split("T")[0];
          }
        }
      }

      if (updatedCtag.Colleague_End_Date) {
        if (updatedCtag.Colleague_End_Date.toDate) {
          // Firestore Timestamp
          const endDate = updatedCtag.Colleague_End_Date.toDate();
          updatedCtag.Colleague_End_Date = endDate.toISOString().split("T")[0];
        } else if (updatedCtag.Colleague_End_Date instanceof Date) {
          // JavaScript Date object
          updatedCtag.Colleague_End_Date =
            updatedCtag.Colleague_End_Date.toISOString().split("T")[0];
        } else if (typeof updatedCtag.Colleague_End_Date === "string") {
          // String - try to convert to proper format
          const dateObj = new Date(updatedCtag.Colleague_End_Date);
          if (!isNaN(dateObj.getTime())) {
            updatedCtag.Colleague_End_Date = dateObj
              .toISOString()
              .split("T")[0];
          }
        }
      }

      setFormData(updatedCtag);
    }
  }, [ctag]);

  // Defensive fallback for formData
  const safeFormData = formData || {};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => {
      let processedValue = value;

      // Convert Credits field to number
      if (name === "Credits") {
        processedValue = value === "" ? "" : parseFloat(value) || 0;
      }

      const newData = {
        ...prevData,
        [name]: processedValue,
      };

      // Auto-calculate Sinclair_Course_and_Subject when related fields change
      // Formula: [@field:Course]+': '+[@field:Subject]
      if (name === "Sinclair_Course" || name === "Sinclair_Subject_Long") {
        const course =
          name === "Sinclair_Course" ? value : newData.Sinclair_Course || "";
        const subject =
          name === "Sinclair_Subject_Long"
            ? value
            : newData.Sinclair_Subject_Long || "";
        newData.Sinclair_Course_and_Subject =
          course && subject ? `${course}: ${subject}` : "";
      }

      // Auto-calculate Colleague_CTAN when CTAN changes
      if (name === "CTAN" && value) {
        const ctan = value.toString();
        if (ctan.length >= 4) {
          const left4 = ctan.substring(0, 4);
          const remaining = ctan.substring(4);
          newData.Colleague_CTAN = `${left4}*${remaining}`;
        } else {
          newData.Colleague_CTAN = "";
        }
      }

      return newData;
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked ? -1 : 0,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (!safeFormData.id) {
        // Ensure document ID is present
        throw new Error("Document ID is missing. Cannot update.");
      }

      // Prepare data for submission, converting date strings to Timestamps
      const submitData = { ...safeFormData };

      // Convert date strings to Firestore Timestamps
      if (
        submitData.Colleague_Start_Date &&
        typeof submitData.Colleague_Start_Date === "string" &&
        submitData.Colleague_Start_Date.trim() !== ""
      ) {
        const startDate = new Date(
          submitData.Colleague_Start_Date + "T12:00:00.000Z"
        );
        submitData.Colleague_Start_Date = Timestamp.fromDate(startDate);
      } else if (
        !submitData.Colleague_Start_Date ||
        submitData.Colleague_Start_Date === ""
      ) {
        submitData.Colleague_Start_Date = null;
      }

      if (
        submitData.Colleague_End_Date &&
        typeof submitData.Colleague_End_Date === "string" &&
        submitData.Colleague_End_Date.trim() !== ""
      ) {
        const endDate = new Date(
          submitData.Colleague_End_Date + "T12:00:00.000Z"
        );
        submitData.Colleague_End_Date = Timestamp.fromDate(endDate);
      } else if (
        !submitData.Colleague_End_Date ||
        submitData.Colleague_End_Date === ""
      ) {
        submitData.Colleague_End_Date = null;
      }

      await updateData(submitData.id, submitData); // Use updateData with document ID
      setLoading(false);
      setSnackbarOpen(true);
      onRecordUpdated(); // Call the callback function to refresh the list
    } catch (error) {
      console.error("Error updating record:", error);
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      if (!safeFormData.id) {
        // Ensure document ID is present
        throw new Error("Document ID is missing. Cannot delete.");
      }
      await deleteData(safeFormData.id); // Use deleteData with document ID
      setLoading(false);
      setSnackbarOpen(true);
      onRecordUpdated(); // Call the callback function to refresh the list
      onClose(); // Close the dialog
    } catch (error) {
      console.error("Error deleting record:", error);
      setLoading(false);
    }
  };

  const handleConfirmDeleteOpen = () => {
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDeleteClose = () => {
    setConfirmDeleteOpen(false);
  };

  const handleConfirmDelete = () => {
    handleConfirmDeleteClose();
    handleDelete();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // The X (and backdrop/Escape, which share the same Dialog onClose) always
  // asks for confirmation first rather than closing immediately, so an
  // in-progress edit isn't lost by an accidental click.
  const handleCloseAttempt = () => {
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseAttempt} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleCloseAttempt}>
          Edit Record
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="CTAN"
                name="CTAN"
                value={safeFormData.CTAN}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="CTAN Name"
                name="CTAN_Name"
                value={safeFormData.CTAN_Name}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="EMIS Code"
                name="EMIS_Code"
                value={safeFormData.EMIS_Code}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="ODE Career Field"
                name="ODE_career_field"
                value={safeFormData.ODE_career_field}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Articulation Number"
                name="Articulation_Number"
                value={safeFormData.Articulation_Number}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Sinclair Course"
                name="Sinclair_Course"
                value={safeFormData.Sinclair_Course}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Sinclair Subject Short"
                name="Sinclair_Subject_Short"
                value={safeFormData.Sinclair_Subject_Short}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Sinclair Subject Long"
                name="Sinclair_Subject_Long"
                value={safeFormData.Sinclair_Subject_Long}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Credits"
                name="Credits"
                type="number"
                inputProps={{
                  step: "0.1",
                  min: "0",
                }}
                value={safeFormData.Credits}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Sinclair Course and Subject"
                name="Sinclair_Course_and_Subject"
                value={safeFormData.Sinclair_Course_and_Subject}
                disabled
                fullWidth
                margin="normal"
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#999",
                    backgroundColor: "#f5f5f5",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="ODHE Portal Note 01"
                name="ODHE_Portal_Note_01"
                value={safeFormData.ODHE_Portal_Note_01}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Colleague CTAN"
                name="Colleague_CTAN"
                value={safeFormData.Colleague_CTAN}
                disabled
                fullWidth
                margin="normal"
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#999",
                    backgroundColor: "#f5f5f5",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Colleague Start Date"
                name="Colleague_Start_Date"
                type="date"
                value={safeFormData.Colleague_Start_Date || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Colleague End Date"
                name="Colleague_End_Date"
                type="date"
                value={safeFormData.Colleague_End_Date || ""}
                onChange={handleChange}
                fullWidth
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Colleague Note 01"
                name="Colleague_Note_01"
                value={safeFormData.Colleague_Note_01}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="TP Office Note 01"
                name="TP_Office_Note_01"
                value={safeFormData.TP_Office_Note_01}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Status A"
                name="Status_A"
                value={safeFormData.Status_A}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={safeFormData.ODHE_Portal_In === -1}
                    onChange={handleCheckboxChange}
                    name="ODHE_Portal_In"
                  />
                }
                label="ODHE Portal In"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={safeFormData.Colleague_TCEQ_In === -1}
                    onChange={handleCheckboxChange}
                    name="Colleague_TCEQ_In"
                  />
                }
                label="Colleague TCEQ In"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={safeFormData.OK_to_Post === -1}
                    onChange={handleCheckboxChange}
                    name="OK_to_Post"
                  />
                }
                label="OK to Post"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions
          onSave={handleSubmit}
          saving={loading}
          onDelete={handleConfirmDeleteOpen}
          deleting={loading}
        />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          message="Record has been updated"
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          ContentProps={{
            sx: {
              backgroundColor: "black",
              color: "white",
            },
          }}
        />
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={handleConfirmDeleteClose}
        onConfirm={handleConfirmDelete}
        deleting={loading}
        message="Are you sure you want to delete this?"
      />
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Edit;
